/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { zodResolver } from '@hookform/resolvers/zod'
import { useMemo } from 'react'
import type { Resolver } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as z from 'zod'

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'

import { SettingsForm } from '../components/settings-form-layout'
import { SettingsPageFormActions } from '../components/settings-page-context'
import { SettingsSection } from '../components/settings-section'
import { useSettingsForm } from '../hooks/use-settings-form'
import { useUpdateOption } from '../hooks/use-update-option'

type PaymentRedirectFormValues = {
  PaymentRedirectURL: string
}

function isPaymentRedirectURL(value: string) {
  const trimmed = value.trim()
  if (!trimmed) return true

  try {
    const url = new URL(trimmed)
    return (
      (url.protocol === 'http:' || url.protocol === 'https:') &&
      Boolean(url.hostname)
    )
  } catch {
    return false
  }
}

export function PaymentRedirectSettingsSection({
  defaultValue,
}: {
  defaultValue: string
}) {
  const { t } = useTranslation()
  const updateOption = useUpdateOption()
  const schema = useMemo(
    () =>
      z.object({
        PaymentRedirectURL: z.string().refine(
          isPaymentRedirectURL,
          t('Enter a complete http:// or https:// payment redirect URL.')
        ),
      }),
    [t]
  )

  const { form, handleSubmit, handleReset, isDirty, isSubmitting } =
    useSettingsForm<PaymentRedirectFormValues>({
      resolver: zodResolver(schema) as Resolver<PaymentRedirectFormValues>,
      defaultValues: { PaymentRedirectURL: defaultValue },
      onSubmit: async (_values, changedFields) => {
        const value = changedFields.PaymentRedirectURL
        if (typeof value !== 'string') return

        await updateOption.mutateAsync({
          key: 'payment_setting.payment_redirect_url',
          value: value.trim(),
        })
      },
    })

  return (
    <SettingsSection title={t('Payment Redirect')}>
      <Form {...form}>
        <SettingsForm onSubmit={handleSubmit} autoComplete='off'>
          <SettingsPageFormActions
            onSave={handleSubmit}
            onReset={handleReset}
            isSaving={updateOption.isPending || isSubmitting}
            isSaveDisabled={!isDirty}
          />
          <FormField
            control={form.control}
            name='PaymentRedirectURL'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('Payment redirect URL')}</FormLabel>
                <FormControl>
                  <Input
                    type='url'
                    placeholder='https://pay.example.com/checkout'
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  {t(
                    'Page opened in a new tab after a user confirms a wallet payment.'
                  )}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </SettingsForm>
      </Form>
    </SettingsSection>
  )
}
